import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { CampaignCreator } from './types';

dayjs.extend(relativeTime);

export function formatCompact(n?: number | null): string {
    if (n == null || Number.isNaN(n)) return '--';
    const abs = Math.abs(n);
    if (abs < 1000) {
        return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
    }
    const units = ['K', 'M', 'B'];
    const order = Math.min(Math.floor(Math.log10(abs) / 3), units.length);
    const scaled = n / Math.pow(1000, order);
    const rounded = Math.round(scaled * 10) / 10;
    const body = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
    return `${body}${units[order - 1]}`;
}

export function formatVIT(n?: number | null): string {
    if (n == null || Number.isNaN(n)) return '--';
    if (Math.abs(n) < 0.005) return '0';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function formatEuro(n?: number | null): string {
    if (n == null) return '--';
    return `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function formatPct(n?: number | null): string {
    if (n == null || Number.isNaN(n)) return '--';
    return `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function formatDateRange(start?: string | null, end?: string | null): string {
    if (!start && !end) return '';
    const a = start ? dayjs(start).format('MMM D, YYYY') : '?';
    const b = end ? dayjs(end).format('MMM D, YYYY') : '?';
    return `${a} – ${b}`;
}

export function formatRefreshed(iso?: string | null): string {
    if (!iso) return 'Never refreshed';
    return `Refreshed ${dayjs(iso).fromNow()}`;
}

export function accountOf(c: CampaignCreator, platformId: number) {
    return c.accounts?.find((a) => a.platform_id === platformId || (
        platformId === 1 && a.platform?.name === 'YouTube'
    ) || (platformId === 2 && a.platform?.name === 'Instagram')
    || (platformId === 3 && a.platform?.name === 'TikTok'));
}

export function totalAudience(c: CampaignCreator): number {
    return (c.accounts ?? []).reduce((sum, a) => sum + (a.following ?? 0), 0);
}

export function audienceTier(followers: number): string {
    if (followers >= 1_000_000) return 'Mega';
    if (followers >= 500_000) return 'Macro';
    if (followers >= 100_000) return 'Mid';
    if (followers >= 10_000) return 'Micro';
    return 'Nano';
}

export function creatorCountry(c: CampaignCreator): string {
    return c.contact_info?.region || c.accounts?.find((a) => a.region)?.region || '';
}

export function avatarSrc(c: CampaignCreator): string | undefined {
    const img = c.accounts?.find((a) => a.image)?.image;
    return img ? `data:image/jpeg;base64,${img}` : undefined;
}

export function dash(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '--';
    return String(value);
}

export const STAGE_OPTIONS = [
    { label: 'Unaware', value: 'unaware' },
    { label: 'Aware', value: 'aware' },
    { label: 'Interested', value: 'interested' },
    { label: 'Negotiating', value: 'negotiating' },
    { label: 'Confirmed', value: 'confirmed' },
    { label: 'Completed', value: 'completed' },
];

export const PLATFORM_LABEL: Record<string, string> = {
    ig: 'Instagram',
    yt: 'YouTube',
    tt: 'TikTok',
};

export const PLATFORM_COLOR: Record<string, string> = {
    ig: '#c13584',
    yt: '#ff6b00',
    tt: '#25f4ee',
};

export function platformColor(name: string): string {
    const key = Object.keys(PLATFORM_LABEL).find((k) => k === name || PLATFORM_LABEL[k] === name);
    return (key && PLATFORM_COLOR[key]) || '#1677ff';
}
